import { isValidMessage, isSupportedVersion } from './codec';
import type { AnyMessage } from './types';

export interface TransportConfig {
  /**
   * Разрешенные origins для безопасности
   * В проде НИКОГДА не должен быть ["*"]
   */
  allowedOrigins: string[];
  /**
   * Target origin для отправки сообщений
   * В проде НИКОГДА не должен быть "*"
   * Для game SDK может быть "*" изначально, но будет автоматически обновлен после первого сообщения
   */
  targetOrigin: string;
  /**
   * Window для отправки (для host это iframe.contentWindow, для game это window.parent)
   */
  targetWindow: Window;
  /**
   * Включить отладочные логи
   */
  debug?: boolean;
  /**
   * Кастомный логгер. По умолчанию `console.log` при `debug=true`.
   */
  logger?: (...args: unknown[]) => void;
}

export type MessageHandler = (message: AnyMessage) => void;

export class Transport {
  public name = 'Transport';

  private config: TransportConfig;
  private handlers: Set<MessageHandler> = new Set();
  private isListening = false;
  private targetOriginLocked = false;

  public constructor(config: TransportConfig) {
    this.config = config;
    this.targetOriginLocked = this.config.targetOrigin !== '*';
    this.validateConfig();
  }

  public listen(): void {
    if (this.isListening) return;
    window.addEventListener('message', this.handleMessageEvent);
    this.isListening = true;
    this.log('Transport started listening');
  }

  public stop(): void {
    if (!this.isListening) return;
    window.removeEventListener('message', this.handleMessageEvent);
    this.isListening = false;
    this.log('Transport stopped listening');
  }

  public send(message: AnyMessage): void {
    try {
      this.config.targetWindow.postMessage(message, this.config.targetOrigin);
      this.log('Sent message:', message);
    } catch (error) {
      console.error('Failed to send message:', error);
    }
  }

  public subscribe(handler: MessageHandler): () => void {
    this.handlers.add(handler);
    return () => this.handlers.delete(handler);
  }

  public updateTargetWindow(targetWindow: Window): void {
    this.config.targetWindow = targetWindow;
  }

  public isConnected(): boolean {
    return this.isListening;
  }

  private validateConfig(): void {
    if (import.meta.env.VITE_APP_ENV !== 'production' || !this.targetOriginLocked) return;

    if (this.config.targetOrigin === '*') {
      throw new Error('targetOrigin="*" is forbidden in production');
    }
    if (this.config.allowedOrigins.includes('*')) {
      throw new Error('allowedOrigins=["*"] is forbidden in production');
    }
  }

  private handleMessageEvent = (event: MessageEvent): void => {
    if (!this.isOriginAllowed(event.origin)) {
      this.log('Rejected message from disallowed origin:', event.origin);
      return;
    }
    if (event.source !== this.config.targetWindow) {
      this.log('Rejected message from unexpected source');
      return;
    }

    const message = event.data;
    if (!isValidMessage(message)) {
      this.log('Rejected invalid message structure:', message);
      return;
    }
    if (!isSupportedVersion(message.v)) {
      this.log('Rejected unsupported protocol version:', message.v);
      return;
    }

    if (this.config.targetOrigin === '*' && !this.targetOriginLocked) {
      this.config.targetOrigin = event.origin;
      this.targetOriginLocked = true;
      this.log('Auto-detected targetOrigin:', event.origin);
    }

    this.log('Received message:', message);

    this.handlers.forEach((handler) => {
      try {
        handler(message);
      } catch (error) {
        console.error('Handler error:', error);
      }
    });
  };

  private isOriginAllowed(origin: string): boolean {
    if (this.config.allowedOrigins.includes('*')) return true;
    return this.config.allowedOrigins.includes(origin);
  }

  private log(...args: unknown[]): void {
    if (!this.config.debug) return;
    const tagged = [`[${this.name}]`, ...args];
    (this.config.logger ?? console.log)(...tagged);
  }
}
